import { customBaseQuery } from '@/lib/baseQuery';
import { ApiResponse } from '@/shared/types';
import { createApi } from '@reduxjs/toolkit/query/react';
import {
  EnsureAssessmentAndCsaResponse,
  EnsureCsaWorkflowDto,
  GetAssessmentWithCsaDto,
} from '../types/workflow';
import { CsaDetail } from '../routes/Recommendation/types/csapayload.type';
// The same copy the document-request page shows, from the same service.
import { WireSavedCsaCopy } from '@/pages/documents-invite/types/api';
import { SavedCsaDocument } from '../routes/RecommendationUpdated/types/csa-pack.types';
import {
  LenderRecommendationResponseType,
  LenderRecommendationType,
} from '../routes/Recommendation/types/recommendation.types';

export const recommendationApi = createApi({
  reducerPath: 'recommendationApi',
  baseQuery: customBaseQuery,
  tagTypes: ['CSA', 'AssessmentDetail'] as const,
  endpoints: (builder) => ({
    ensureAssessmentAndCsa: builder.mutation<
      ApiResponse<EnsureAssessmentAndCsaResponse>,
      EnsureCsaWorkflowDto
    >({
      query: (body) => ({
        url: `/v1/mortgage-workflow/assessment-csa/ensure`,
        method: 'POST',
        body, // if your customBaseQuery is axios-based, this might need to be `data: body`
      }),
      // Only invalidate on a successful write. RTK Query invalidates on
      // rejected mutations too, so without this guard a failing ensure (e.g. a
      // 403) invalidates CSA/AssessmentDetail, which refetches the detail query,
      // whose fetching→settled transition triggers another autosave → another
      // ensure → infinite request loop that trips the server throttler.
      invalidatesTags: (_res, err, arg) =>
        err
          ? []
          : [
              { type: 'CSA', id: arg.sourceAssessmentId },
              { type: 'AssessmentDetail', id: arg.sourceAssessmentId },
            ],
    }),

    getAssessmentDetailWithCsa: builder.query<
      ApiResponse<{
        assessment: any;

        csa: CsaDetail;
      }>,
      GetAssessmentWithCsaDto
    >({
      query: (body) => ({
        url: `/v1/mortgage-workflow/assessment/detail`,
        method: 'POST',
        body, // axios baseQuery might need `data: body`
      }),
      providesTags: (_res, _err, arg) => [
        { type: 'CSA', id: arg.sourceAssessmentId },
        { type: 'AssessmentDetail', id: arg.sourceAssessmentId },
      ],
    }),

    /**
     * The latest CSA PDF saved when a CA downloaded it, or null when nothing
     * has been saved. Keyed by the assessment's uuid, not the source id.
     *
     * No tag: the save happens on the server just after the PDF is sent, so
     * no mutation here knows when it lands. The caller refetches after a
     * download instead, and polls to keep the five-minute link usable.
     */
    getSavedCsaCopy: builder.query<WireSavedCsaCopy | null, string>({
      query: (assessmentId) =>
        `/v1/mortgage-workflow/assessment/${assessmentId}/saved-csa`,
      transformResponse: (r: ApiResponse<WireSavedCsaCopy | null>) => r.data,
    }),

    /** The latest saved copy of the combined CSA and each pack document; links last five minutes. */
    getSavedCsaDocuments: builder.query<SavedCsaDocument[], string>({
      query: (assessmentId) =>
        `/v1/mortgage-workflow/assessment/${assessmentId}/saved-csa-documents`,
      transformResponse: (r: ApiResponse<SavedCsaDocument[]>) => r.data,
    }),
  }),
});

export const {
  useEnsureAssessmentAndCsaMutation,
  useGetAssessmentDetailWithCsaQuery,
  useGetSavedCsaCopyQuery,
  useGetSavedCsaDocumentsQuery,
} = recommendationApi;
